const BASE_FONT_SIZE = 16;
const UNIT_PX = 'px';
const UNIT_EM = 'em';
const UNIT_REM = 'rem';

// https://regex101.com/r/zvY2bu/1
const DIGIT_REGEX = new RegExp(String.raw`-?\d+(?:\.\d+|\d*)`);
const UNIT_REGEX = new RegExp(`${UNIT_PX}|${UNIT_EM}|${UNIT_REM}`);
function getUnit(value = '') {
  const unit = value.match(new RegExp(`${DIGIT_REGEX.source}(${UNIT_REGEX.source})`));
  return unit && unit[1];
}
function toPx(value = '') {
  const unit = getUnit(value);
  if (!unit) return value;
  if (unit === UNIT_PX) {
    return value;
  }
  if (unit === UNIT_EM || unit === UNIT_REM) {
    return `${parseFloat(value) * BASE_FONT_SIZE}${UNIT_PX}`;
  }
}
function toPxs(value) {
  return value.replace(new RegExp(`${DIGIT_REGEX.source}(${UNIT_EM}|${UNIT_REM})`, 'g'), emOrRem => toPx(emOrRem) ?? emOrRem);
}
function toEm(value = '', fontSize = BASE_FONT_SIZE) {
  const unit = getUnit(value);
  if (!unit) return value;
  if (unit === UNIT_EM) {
    return value;
  }
  if (unit === UNIT_PX) {
    return `${parseFloat(value) / fontSize}${UNIT_EM}`;
  }
  if (unit === UNIT_REM) {
    return `${parseFloat(value) * BASE_FONT_SIZE / fontSize}${UNIT_EM}`;
  }
}
function toRem(value = '') {
  const unit = getUnit(value);
  if (!unit) return value;
  if (unit === UNIT_REM) {
    return value;
  }
  if (unit === UNIT_EM) {
    return `${parseFloat(value)}${UNIT_REM}`;
  }
  if (unit === UNIT_PX) {
    return `${parseFloat(value) / BASE_FONT_SIZE}${UNIT_REM}`;
  }
}
function rem(value) {
  return value.replace(new RegExp(`${DIGIT_REGEX.source}(${UNIT_PX})`, 'g'), px => toRem(px) ?? px);
}
function tokenGroupToRems(metaTokenGroup) {
  return Object.fromEntries(Object.entries(metaTokenGroup).map(([tokenName, tokenProperties]) => [tokenName, {
    ...tokenProperties,
    value: rem(tokenProperties.value)
  }])
  // We loose the `metaTokenGroup` inference after transforming the object with
  // `Object.fromEntries()` and `Object.entries()`. Thus, we cast the result
  // back to `T` since we are simply converting the `value` from px to rem.
  );
}
function createVarName(tokenName) {
  return `--p-${tokenName}`;
}
function createVar(tokenName) {
  return `var(${createVarName(tokenName)})`;
}
function getTokenNames(theme) {
  return Object.values(theme).flatMap(tokenGroup => Object.keys(tokenGroup));
}

/**
 * Allowed Polaris token custom properties.
 *
 * Result: ['--p-color-bg', '--p-color-text', etc...]
 */
function getThemeVarNames(theme) {
  return getTokenNames(theme).map(createVarName);
}

/**
 * Alias direction used for composing Polaris `breakpoints` utilities.
 */

/**
 * A collection of directional media conditions for a given Polaris `breakpoints` alias.
 */

/**
 * Media conditions for all Polaris `breakpoints` aliases.
 */

function getMediaConditions(breakpoints) {
  const breakpointEntries = Object.entries(breakpoints);
  const lastBreakpointIndex = breakpointEntries.length - 1;
  return Object.fromEntries(breakpointEntries.map((entry, index) => {
    const [breakpointsTokenName, breakpoint] = entry;
    const upMediaCondition = getUpMediaCondition(breakpoint);
    const downMediaCondition = getDownMediaCondition(breakpoint);
    const onlyMediaCondition = index === lastBreakpointIndex ? upMediaCondition : `${upMediaCondition} and ${getDownMediaCondition(breakpointEntries[index + 1][1])}`;
    return [breakpointsTokenName, {
      // Media condition for the current breakpoint and up
      up: upMediaCondition,
      // Media condition for current breakpoint and down
      down: downMediaCondition,
      // Media condition for only the current breakpoint
      only: onlyMediaCondition
    }];
  }));
}
function getUpMediaCondition(breakpoint) {
  return `(min-width: ${toEm(breakpoint)})`;
}

/**
 * Down media condition breakpoints are being subtracted by 0.04px to prevent
 * them from overwriting up media queries. We experimented with multiple offsets
 * and felt that 0.04px would be the safest across different pixel densities,
 * while being representable in ems with 4 decimal places of precision.
 */
function getDownMediaCondition(breakpoint) {
  const offsetBreakpoint = parseFloat(toPx(breakpoint) ?? '') - 0.04;
  return `(max-width: ${toEm(`${offsetBreakpoint}px`)})`;
}
const tokenGroupNamesToRems = ['border', 'breakpoints', 'font', 'height', 'shadow', 'space', 'text', 'width'];

/**
 * Mimics the behavior of an identity function:
 * - Validates the input matches the `MetaThemeShape` type exactly
 * - Converts all `px` values to `rem`
 * - Infers all members
 *
 * @example
 * ```
 * const example = createMetaThemeBase({
 *   color: {
 *     bg: {value: '#fff'},
 *   },
 * })
 * ```
 *
 * Where `typeof example` is inferred as `{ color: { bg: { value: string } } }`
 */
function createMetaThemeBase(metaTheme) {
  return Object.fromEntries(Object.entries(metaTheme).map(([tokenGroupName, tokenGroup]) => [tokenGroupName, tokenGroupNamesToRems.includes(tokenGroupName) ? tokenGroupToRems(tokenGroup) : tokenGroup]));
}

export { createMetaThemeBase, createVar, createVarName, getMediaConditions, getThemeVarNames, getTokenNames, getUnit, rem, toEm, toPx, toPxs, toRem, tokenGroupNamesToRems, tokenGroupToRems };
